import type { TFunction } from 'i18next';

import { DEFAULT_OPERATORS } from '@/components/organisms/SessionsInfiniteTable/filters/constants';
import type { InsightCategory, InsightFilter, LogsAnalytics } from '@/shared/types/analytics';
import { formatDate } from '@/util/dateUtils';
import { urlSafeBase64Encode } from '@/util/util';

export const aiAgentLink = (slug: string, brainParentId: string) =>
  `/${slug}/ai-agents/${brainParentId}`;

const environmentLink = (slug: string, deskId: string) => `/${slug}/environments/${deskId}`;

const knowledgeBaseLink = (slug: string, collectionId: string) =>
  `/${slug}/knowledge-bases/${collectionId}`;

const EMPTY_GROUP_KEY = '∅';

const ENTITY_LINK_BUILDERS: Record<string, (slug: string, key: string) => string> = {
  brain_parent_id: aiAgentLink,
  desk_id: environmentLink,
  collection_id: knowledgeBaseLink,
};

export const DELETED_ENTITY_LABEL_KEYS: Record<string, string> = {
  brain_parent_id: 'analytics.deleted_ai_agent',
  desk_id: 'analytics.deleted_env',
  collection_id: 'analytics.deleted_knowledge_base',
  datasource_id: 'analytics.deleted_datasource',
  document_id: 'analytics.deleted_document',
  agent_id: 'common.unknown_user',
};

export const groupDisplayLabel = (
  column: string,
  resolved: { label: string; isMissing: boolean } | undefined,
  t: TFunction
): string => {
  const deletedKey = DELETED_ENTITY_LABEL_KEYS[column];
  if (resolved?.isMissing && deletedKey) {
    return t(deletedKey);
  }
  return resolved?.label ?? '';
};

export const groupValueLink = (
  slug: string | undefined,
  column: string,
  key: string,
  isMissing: boolean
): string | null => {
  if (!slug || !key || key === EMPTY_GROUP_KEY || isMissing) {
    return null;
  }
  return ENTITY_LINK_BUILDERS[column]?.(slug, key) ?? null;
};

const FILTERABLE_INSIGHT_CATEGORIES: ReadonlySet<string> = new Set([
  'categorical',
  'string',
  'boolean',
]);

const insightFilterValue = (category: InsightCategory, bucketKey: string) => {
  if (category === 'boolean') {
    return bucketKey === 'true';
  }
  return bucketKey;
};

export const buildInsightFilter = (
  insightName: string,
  category: string,
  bucketKey: string
): InsightFilter | null => {
  if (!FILTERABLE_INSIGHT_CATEGORIES.has(category) || !bucketKey || bucketKey === EMPTY_GROUP_KEY) {
    return null;
  }
  const insightCategory = category as InsightCategory;
  return {
    id: insightName,
    category: insightCategory,
    operator: DEFAULT_OPERATORS[category] ?? 'in',
    values: [insightFilterValue(insightCategory, bucketKey)],
  };
};

export const CONVERSATION_COUNT_METRIC_IDS: ReadonlySet<string> = new Set([
  'ai_conversations',
  'ai_goal_achieved',
  'ai_user_sentiment',
  'ai_csat_brain',
]);

const BUCKET_FILTERS: Record<string, (value: number) => LogsAnalytics> = {
  goal_achieved: (value) => ({ type: 'goalAchieved', goalAchieved: [value] }),
  user_sentiment: (value) => ({ type: 'userSentiment', userSentiment: [value] }),
  csat_brain: (value) => ({ type: 'csatBrain', csatBrain: [value], csatBrainMode: 'in' }),
};

export const buildBucketFilter = (column: string, key: string): LogsAnalytics | null => {
  const build = BUCKET_FILTERS[column];
  const value = Number(key);
  if (!build || !Number.isInteger(value) || value <= 0) {
    return null;
  }
  return build(value);
};

export const insightUrlFilter = (insightFilter: InsightFilter): LogsAnalytics => ({
  type: 'insightFilters',
  insightFilters: [insightFilter],
});

export type ConversationsLinkArgs = {
  slug: string | undefined;
  brainParentId?: string;
  deskId?: string;
  channel?: string;
  rowFilter?: LogsAnalytics | null;
  timeRange: { start: string; end: string };
};

export const conversationsLink = ({
  slug,
  brainParentId,
  deskId,
  channel,
  rowFilter,
  timeRange,
}: ConversationsLinkArgs): string | null => {
  const agentId = brainParentId && brainParentId !== EMPTY_GROUP_KEY ? brainParentId : undefined;
  if (!slug || !agentId) {
    return null;
  }

  const filters: LogsAnalytics[] = [];
  if (rowFilter) {
    filters.push(rowFilter);
  }
  if (deskId && deskId !== EMPTY_GROUP_KEY) {
    filters.push({ type: 'environments', deskIds: [deskId] });
  }
  if (channel && channel !== EMPTY_GROUP_KEY) {
    filters.push({ type: 'channels', channels: [channel] });
  }

  const startDate = formatDate(timeRange.start, 'yyyy-MM-dd');
  const endDate = formatDate(timeRange.end, 'yyyy-MM-dd');
  const date = { startDate, endDate, selection: { type: 'custom', startDate, endDate } };

  const params = new URLSearchParams({ date: urlSafeBase64Encode(JSON.stringify(date)) });
  if (filters.length > 0) {
    params.set('filters', urlSafeBase64Encode(JSON.stringify(filters)));
  }

  return `/${slug}/ai-agents/${agentId}/logs?${params.toString()}`;
};
